<template>
	<div class="message-text-container">
		<UnfurlCard v-if="urlAtStart" :resource="message.resource" />
		<div
			v-if="hasText || message.isEdited"
			:class="{ 'position-relative': canTranslate }"
		>
			<div
				:class="{
					'message-content html-div pre-wrap preWrapHtml': true,
					translated: message.isTranslated,
					'emojis-only': isOnlyEmoji,
					'm-t-5': needsTopMargin,
				}"
				data-cy="message-content"
			>
				<div class="message-parts-container">
					<component
						v-for="(part, i) in messageParts"
						v-bind="part.props || {}"
						:key="i"
						:is="part.is"
						@click="part.onClick || null"
						data-private
						>{{ part.text }}</component
					>{{
						hasText && (message.isEdited || showGlobe)
							? '\xa0' /* prevent breaking space */
							: ''
					}}<EditedFlag
						v-if="message.isEdited"
						:date="message.user_edited_at"
					/>{{
						message.isEdited && showGlobe
							? '\xa0' /* prevent breaking space */
							: ''
					}}<Tippy
						v-if="showGlobe"
						class="pointer"
						:options="tipOptions"
						@click="onGlobeClick"
						tabindex="-1"
						:content="translationTip"
					>
						<EarthIcon class="globe" :active="message.isTranslated" />
					</Tippy>
				</div>
			</div>
		</div>
		<div
			id="transLabel"
			v-if="isTranslating"
			v-text="$t('chat_translation_inprogress')"
		/>
		<UnfurlCard v-if="urlAtEnd" :resource="message.resource" />
	</div>
</template>
<script>
import { post } from '../../helpers/api'
import { inMeeting } from '@/helperMethods'
import { EarthIcon } from '@/icons'
import UnfurlCard from './UnfurlCard'
import Mention from './Mention'
import { getLanguagesData } from '@/helpers/firebase'
import * as translations from '@/store/translations'
import { containsOnlyEmoji } from '@/helpers/emojis'
import { parseMessageParts, partType } from './utils'
import { setMessage, updateMessage } from '@/store'
import { getInviteCodeFromUrl } from '@/components/Invite'
import { Tippy } from '@/components/common'
import EditedFlag from './EditedFlag'

const tipOptions = {
	arrow: true,
	theme: 'bluewhite',
	animation: 'scale',
	duration: [100, 100],
	interactive: true,
}

export default {
	name: 'MessageText',
	props: ['message', 'currentUser'],
	components: { EarthIcon, EditedFlag, Tippy, UnfurlCard },
	data() {
		return {
			urlAtStart: false,
			urlAtEnd: false,
			isTranslating: false,
			partType,
			tipOptions,
		}
	},

	mounted() {
		this.determineTranslationState()
	},

	methods: {
		determineTranslationState() {
			const { autotranslate, id: userId } = this.currentUser
			const { id, messagetrans, user_id } = this.message
			const showTranslation =
				translations.check(id) ||
				(this.canTranslate &&
					autotranslate &&
					messagetrans &&
					userId !== user_id)
			this.setTranslated(showTranslation)
		},

		onGlobeClick() {
			this.isTranslating = true
			if (translations.check(this.message.id)) {
				translations.remove(this.message.id)
			} else {
				translations.add(this.message.id)
			}
			if (this.message.isTranslated) {
				this.setTranslated(false)
				this.isTranslating = false
				return
			}
			this.translateMessage()
		},

		onEmbeddedLinkClick(e, href) {
			const inviteCode = getInviteCodeFromUrl(href)
			if (inviteCode && !inMeeting()) {
				e.preventDefault()
				EventBus.$emit('openGroupInviteLink', inviteCode)
			}
		},

		async translateMessage() {
			const { messagetrans } = this.message
			const { language } = this.currentUser

			// Check for Translation first...
			if (messagetrans) {
				this.setTranslated(true)
				this.isTranslating = false
				return
			}

			try {
				const { data } = await post('/api/v1/message.translate', {
					message_id: this.message.id,
					lang: language,
				})
				setMessage(data.message)
				this.$nextTick(function () {
					this.setTranslated(true)
					this.isTranslating = false
				})
			} catch (error) {
				console.error(error)
			}
		},

		setTranslated(isTranslated) {
			if (this.message.isTranslated !== isTranslated) {
				updateMessage(this.message.id, { isTranslated })
			}
		},
	},

	watch: {
		'message.messagetrans': {
			handler(curr, prev) {
				if (curr && !prev) {
					this.determineTranslationState()
				}
			},
			deep: true,
		},
	},

	computed: {
		hasText() {
			return this.messageParts.length > 0
		},

		isEditedWithoutText() {
			return this.message.isEdited && !this.hasText
		},

		showGlobe() {
			return this.canTranslate && !this.isTranslating
		},

		needsTopMargin() {
			return (
				(this.message.task || this.message.hasMedia) &&
				!this.isEditedWithoutText
			)
		},

		canTranslate() {
			const { language, languages } = this.currentUser
			if (!languages || !this.message.lang) return false
			return ![...languages, language].includes(this.message.lang)
		},

		messageParts() {
			return parseMessageParts(this.message)
				.map(({ type, userId, user, isMe, atStart, atEnd, href, text }) => {
					switch (type) {
						case partType.MENTION:
							// return <Mention userId={userId} user={user} isMe={isMe} />
							return { is: Mention, props: { userId, user, isMe } }
						case partType.LINK_CARD:
							this.urlAtStart = atStart
							this.urlAtEnd = atEnd
							return null
						case partType.HYPERLINK:
							return {
								is: 'a',
								props: {
									class: 'linkified',
									href,
									target: '_blank',
									tabindex: '-1',
								},
								onClick: (e) => this.onEmbeddedLinkClick(e, href),
								text,
							}
						case partType.TEXT:
							// return text
							return { is: 'span', text }
						default:
							// ignore any unknown types
							return null
					}
				})
				.filter((p) => p)
				.map((part, i, array) => {
					// this is to avoid unwanted whitespace if
					// parts are at start or end of the message
					if (array.length === 1 && part.trimStart)
						return part.trimStart().trimEnd()
					if (i === 0 && part.trimStart) return part.trimStart()
					if (i === array.length - 1 && part.trimEnd) return part.trimEnd()
					return part
				})
		},

		translationTip() {
			const languages = getLanguagesData()
			const { lang, isTranslated } = this.message
			if (!lang || !languages.langs[lang]) return null
			const name =
				languages.langs[lang].localizednames[translations.getMyLanguage()]
			if (isTranslated) {
				return this.$t('chat_translation_translatedfrom_click', {
					Language: name,
				})
			} else {
				return this.$t('chat_translation_translatefrom_click', {
					Language: name,
				})
			}
		},

		isOnlyEmoji() {
			const { message = '' } = this.message
			if (!message) return false
			return containsOnlyEmoji(message, 100)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.message-content,
:deep(.message-content) * {
	outline: none;
	user-select: text;
}
.message-content {
	width: max-content;
	user-select: text;
	outline: 0;
	max-width: 100%;
}

.globe {
	display: inline-block;
	height: 16px;
	white-space: nowrap;
	vertical-align: middle;
	font-size: 14px;
	color: var(--neutral-300);
}

.translated {
	background-color: rgba(73, 164, 218, 0.15);
	border-radius: 8px;
	padding-left: 4px;
	padding-right: 4px;
	margin-left: -4px;
	padding-top: 1px;
	padding-bottom: 1px;
	margin-top: -1px;
	margin-bottom: -1px;
}

@include overrideScheme('dark') {
	.globe {
		color: var(--neutral-400) !important;
	}
}
</style>
<style lang="scss">
.tippy-tooltip {
	background-color: #67686a;
	color: white;
	font-size: 12px;
	padding: 3px 8px;
	border-radius: 5px;
}
</style>
